/**
 * Servicio centralizado para todas las llamadas a la API
 * Centraliza la lógica de comunicación con el backend
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

/**
 * Configuración base para las peticiones fetch
 */
const defaultHeaders = {
    'Content-Type': 'application/json',
};

/**
 * Función helper para manejar respuestas de la API
 * @param {Response} response - Respuesta de fetch
 * @returns {Promise<any>} - Datos parseados o error
 */
const handleApiResponse = async (response) => {
    if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData || 'Error en la petición a la API');
    }
    return response.json();
};

/**
 * Servicios relacionados con usuarios y categorías
 */
export const usuarioService = {
    /**
     * Obtiene las categorías de usuario por NIF
     * @param {string} nif - NIF del usuario
     * @returns {Promise<Array>} - Lista de categorías del usuario
     */
    async getUsuarioCategoria(nif) {
        if (!nif) {
            throw new Error('El NIF està buit o no és vàlid');
        }

        const response = await fetch(`${API_BASE_URL}/api/db/getUsuarioCategoria/${nif}`, {
            method: 'GET',
            headers: defaultHeaders,
        });

        return handleApiResponse(response);
    },

    /**
     * Actualiza la fecha de descarga para una categoría específica
     * @param {string} nif - NIF del usuario
     * @param {string} idCategoria - ID de la categoría
     * @returns {Promise<void>}
     */
    async updateFechaDescarga(nif, idCategoria) {
        if (!nif || !idCategoria) {
            throw new Error('NIF o ID de categoría no proporcionados');
        }

        const response = await fetch(`${API_BASE_URL}/api/db/updateFechaDescarga/${idCategoria}/${nif}`, {
            method: 'POST',
            headers: defaultHeaders,
        });

        if (!response.ok) {
            throw new Error('Error al guardar la data de descàrrega');
        }
    },
};

/**
 * Servicios relacionados con descargas
 */
export const descargaService = {
    /**
     * Descarga un archivo específico
     * @param {string} directorioDescarga - Directorio de descarga
     * @param {string} nif - NIF del usuario
     * @returns {Promise<Blob>} - Archivo como blob
     */
    async descargarArchivo(directorioDescarga, nif) {
        if (!directorioDescarga || !nif) {
            throw new Error('Parámetros de descarga no proporcionados');
        }

        const response = await fetch(`${API_BASE_URL}/api/descarga/${directorioDescarga}/${nif}`, {
            method: 'GET',
            headers: defaultHeaders,
        });

        if (!response.ok) {
            throw new Error('Error a la descàrrega');
        }

        return response.blob();
    },

    /**
     * Procesa la descarga completa: obtiene el archivo y lo descarga
     * @param {string} directorioDescarga - Directorio de descarga
     * @param {string} categoria - Categoría del archivo
     * @param {string} nif - NIF del usuario
     * @returns {Promise<void>}
     */
    async procesarDescarga(directorioDescarga, categoria, nif) {
        try {
            const blob = await this.descargarArchivo(directorioDescarga, nif);
            
            // Crear enlace de descarga
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${categoria}_${nif}`;
            link.setAttribute('aria-label', `Descarregar fitxer ${categoria}`);
            
            // Ejecutar descarga
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error en el procesamiento de la descarga:', error);
            throw error;
        }
    },
};

/**
 * Servicios de configuración (públicos, sin autenticación)
 */
export const configService = {
    /**
     * Obtiene la información de login (descripción + items) desde la BBDD.
     * @returns {Promise<{ descripcion: string, items: string[] }>}
     */
    async getLoginInfo() {
        const response = await fetch(`${API_BASE_URL}/api/config/login-info`, {
            method: 'GET',
            headers: defaultHeaders,
        });
        return handleApiResponse(response);
    },
};

/**
 * Servicios relacionados con autenticación
 */
export const authService = {
    /**
     * Realiza el login del usuario
     * @param {string} user - Usuario/NIF
     * @param {string} password - Contraseña
     * @returns {Promise<Object>} - Respuesta del login
     */
    async login(user, password) {
        if (!user || !password) {
            throw new Error('Usuari i contrasenya són obligatoris');
        }

        const options = {
            method: 'POST',
            body: JSON.stringify({ user, password }),
            headers: { 
                'Content-Type': 'application/json; charset=UTF-8',
            },
        };

        const response = await fetch(`${API_BASE_URL}/api/login`, options);
        
        if (!response.ok) {
            const contentType = response.headers.get('content-type') || '';
            const raw = await response.text();
            const data = raw && contentType.includes('application/json') ? JSON.parse(raw) : null;
            const error = (data && data.message) || response.status;
            throw new Error(error);
        }

        return response;
    },
};

/**
 * Servicio principal que combina operaciones relacionadas
 */
export const gestorService = {
    /**
     * Obtiene datos del usuario y sus categorías
     * @param {string} nif - NIF del usuario
     * @returns {Promise<Array>} - Datos del usuario
     */
    async obtenerDatosUsuario(nif) {
        return usuarioService.getUsuarioCategoria(nif);
    },

    /**
     * Ejecuta una descarga completa: descarga archivo y actualiza fecha
     * @param {string} directorioDescarga - Directorio de descarga
     * @param {string} categoria - Categoría del archivo
     * @param {string} nif - NIF del usuario
     * @param {string} idCategoria - ID de la categoría
     * @returns {Promise<void>}
     */
    async ejecutarDescargaCompleta(directorioDescarga, categoria, nif, idCategoria) {
        try {
            // Ejecutar descarga y actualización en paralelo
            await Promise.all([
                descargaService.procesarDescarga(directorioDescarga, categoria, nif),
                usuarioService.updateFechaDescarga(nif, idCategoria)
            ]);
        } catch (error) {
            console.error('Error en la descarga completa:', error);
            throw error;
        }
    },
};

const apiService = {
    usuarioService,
    descargaService,
    gestorService,
    authService,
    configService,
};

export default apiService;
